import React, { useEffect } from 'react';

// Main Aurora Background Component (keeping for backwards compatibility)
const AuroraBackground = ({ children, className = "" }) => {
  return (
    <div className={`relative overflow-hidden ${className}`}>
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute -inset-[10px] opacity-50"
          style={{
            background: `
              radial-gradient(ellipse at top left, rgba(120, 119, 198, 0.3), transparent 50%),
              radial-gradient(ellipse at bottom right, rgba(255, 119, 198, 0.3), transparent 50%),
              radial-gradient(ellipse at top right, rgba(120, 198, 255, 0.3), transparent 50%)
            `,
            backgroundSize: '160% 160%',
            animation: 'gradientFlow 20s ease-in-out infinite'
          }}
        />
      </div>
      
      <div className="relative z-10">
        {children}
      </div>
      
      <style>{`
        @keyframes gradientFlow {
          0% { transform: scale(1.05) translate(0, 0); }
          50% { transform: scale(1.1) translate(-5%, -5%); }
          100% { transform: scale(1.05) translate(0, 0); }
        }
      `}</style>
    </div>
  );
};

// Interactive Aurora Background - SMOOTH GRADIENT + DOT GRID
export const AuroraBackgroundInteractive = ({ 
  children, 
  className = "",
  variant = "dark",
  intensity = "medium",
  animate = true,
  interactive = false // Disabled by default now
}) => {

  const variants = {
    light: {
      colors: '#e0f2fe, #bae6fd, #7dd3fc, #38bdf8, #0ea5e9',
      interactive: "rgba(186, 230, 253, 0.5)"
    },
    dark: {
      // Steel blue / slate tones from racing simulator - with LIGHTER blue-gray tones
      colors: '#1e293b, #334155, #475569, #64748b, #94a3b8, #cbd5e1, #94a3b8, #64748b, #475569, #334155',
      interactive: "rgba(100, 116, 139, 0.25)"
    },
    darkBlue: {
      colors: '#1e3a8a, #1e40af, #1d4ed8, #2563eb, #3b82f6',
      interactive: "rgba(59, 130, 246, 0.3)"
    }
  };

  const currentVariant = variants[variant] || variants.dark;

  return (
    <div className={`relative overflow-hidden ${className}`}>
      {/* Base layer - solid dark background */}
      <div className="absolute -inset-20 min-h-[120vh] min-w-[120vw] bg-black" />
      
      {/* Dot grid pattern overlay - MORE VISIBLE */}
      <div
        className="absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage: `radial-gradient(circle, rgba(255, 255, 255, 0.5) 1px, transparent 1px)`,
          backgroundSize: '32px 32px'
        }}
      />
      
      {/* Animated gradient layer - NO HARSH LINES */}
      {animate && (
        <div 
          className="absolute inset-0"
          style={{
            background: `linear-gradient(-45deg, ${currentVariant.colors})`,
            backgroundSize: '400% 400%',
            animation: 'gradientShift 15s ease infinite',
            opacity: intensity === 'light' ? 0.15 : intensity === 'medium' ? 0.25 : 0.35
          }}
        />
      )}
      
      {/* Subtle texture overlay */}
      <div 
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' /%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' /%3E%3C/svg%3E")`,
          mixBlendMode: 'overlay'
        }}
      />
      
      {/* Subtle vignette */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at center, transparent 0%, rgba(0, 0, 0, 0.4) 100%)',
          pointerEvents: 'none'
        }}
      />
      
      {/* Content */}
      <div className="relative z-10">
        {children}
      </div>
      
      {/* Smooth gradient animation - inspired by Stripe/Luma */}
      <style>{`
        @keyframes gradientShift {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }
      `}</style>
    </div>
  );
};

// NEW: Animated Dots Background - Premium particle effect
export const AnimatedDotsBackground = ({ 
  children, 
  className = "",
  dotCount = 80,
  dotColor = "rgba(100, 116, 139, 0.6)",
  lineColor = "rgba(100, 116, 139, 0.2)"
}) => {
  const canvasRef = React.useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let dots = [];

    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Create dots
    class Dot {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.vx = (Math.random() - 0.5) * 0.3;
        this.vy = (Math.random() - 0.5) * 0.3;
        this.radius = Math.random() * 1.5 + 0.5;
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;

        // Bounce off edges
        if (this.x < 0 || this.x > canvas.width) this.vx *= -1;
        if (this.y < 0 || this.y > canvas.height) this.vy *= -1;
      }

      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
        ctx.fillStyle = dotColor;
        ctx.fill();
      }
    }

    // Initialize dots
    for (let i = 0; i < dotCount; i++) {
      dots.push(new Dot());
    }

    // Animation loop
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Update and draw dots
      dots.forEach(dot => {
        dot.update();
        dot.draw();
      });

      // Draw lines between close dots
      for (let i = 0; i < dots.length; i++) {
        for (let j = i + 1; j < dots.length; j++) {
          const dx = dots[i].x - dots[j].x;
          const dy = dots[i].y - dots[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 150) {
            ctx.beginPath();
            ctx.moveTo(dots[i].x, dots[i].y);
            ctx.lineTo(dots[j].x, dots[j].y);
            ctx.strokeStyle = lineColor;
            ctx.lineWidth = 1 - distance / 150;
            ctx.stroke();
          }
        }
      }

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, [dotCount, dotColor, lineColor]);

  return (
    <div className={`relative overflow-hidden ${className}`}>
      {/* Black background */}
      <div className="absolute inset-0 bg-black" />
      
      {/* Animated dots canvas */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0"
        style={{ opacity: 0.8 }}
      />
      
      {/* Content */}
      <div className="relative z-10">
        {children}
      </div>
    </div>
  );
};

// NEW: Grid Lines Background - Modern tech aesthetic
export const GridLinesBackground = ({ 
  children, 
  className = "",
  gridSize = 50,
  lineColor = "rgba(203, 213, 225, 0.08)"
}) => {
  return (
    <div className={`relative overflow-hidden ${className}`}>
      {/* Black background */}
      <div className="absolute inset-0 bg-black" />
      
      {/* Gradient overlay */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(-45deg, #334155, #475569, #64748b, #475569, #334155, #1e293b)',
          backgroundSize: '400% 400%',
          animation: 'gradientShift 15s ease infinite',
          opacity: 0.25
        }}
      />
      
      {/* Grid lines */}
      <div 
        className="absolute inset-0"
        style={{
          backgroundImage: `
            linear-gradient(${lineColor} 1px, transparent 1px),
            linear-gradient(90deg, ${lineColor} 1px, transparent 1px)
          `,
          backgroundSize: `${gridSize}px ${gridSize}px`
        }}
      />
      
      {/* Subtle vignette */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at center, transparent 0%, rgba(0, 0, 0, 0.6) 100%)'
        }}
      />
      
      {/* Content */}
      <div className="relative z-10">
        {children}
      </div>
      
      <style>{`
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
    </div>
  );
};

// AuroraBackgroundPro (keeping for backwards compatibility)
export const AuroraBackgroundPro = ({ 
  children, 
  className = "",
  variant = "default",
  intensity = "medium",
  animate = true
}) => {
  const variants = {
    default: {
      gradient1: "rgba(120, 119, 198, 0.3)",
      gradient2: "rgba(255, 119, 198, 0.3)",
      gradient3: "rgba(120, 198, 255, 0.3)"
    },
    cool: {
      gradient1: "rgba(59, 130, 246, 0.3)",
      gradient2: "rgba(6, 182, 212, 0.3)",
      gradient3: "rgba(99, 102, 241, 0.3)"
    },
    dark: {
      gradient1: "rgba(147, 51, 234, 0.2)",
      gradient2: "rgba(37, 99, 235, 0.2)",
      gradient3: "rgba(79, 70, 229, 0.2)"
    }
  };
  
  const opacities = {
    light: "opacity-30",
    medium: "opacity-50",
    strong: "opacity-70"
  };
  
  const currentVariant = variants[variant];
  const currentOpacity = opacities[intensity];
  
  return (
    <div className={`relative overflow-hidden ${className}`}>
      <div className="absolute inset-0 bg-gradient-to-br from-white via-gray-50 to-gray-100" />
      <div className="absolute inset-0 overflow-hidden">
        <div 
          className={`absolute -inset-[10px] ${currentOpacity}`}
          style={{
            background: `
              radial-gradient(ellipse at top left, ${currentVariant.gradient1}, transparent 50%),
              radial-gradient(ellipse at bottom right, ${currentVariant.gradient2}, transparent 50%),
              radial-gradient(ellipse at top right, ${currentVariant.gradient3}, transparent 50%)
            `
          }}
        />
      </div>
      <div className="relative z-10">
        {children}
      </div>
    </div>
  );
};

// Simplified static version
export const AuroraBackgroundStatic = ({ children, className = "" }) => {
  return (
    <div className={`relative overflow-hidden ${className}`}>
      <div 
        className="absolute inset-0"
        style={{
          background: `
            linear-gradient(to bottom right, #ffeaa7, transparent),
            linear-gradient(to top left, #dfe6e9, transparent),
            radial-gradient(ellipse at top, #e17055, transparent),
            radial-gradient(ellipse at bottom, #74b9ff, transparent)
          `,
          opacity: 0.3
        }}
      />
      <div className="relative z-10">
        {children}
      </div>
    </div>
  );
};

export default AuroraBackground;